import * as THREE from 'three';
import { EngineDemoApp } from '../engine/apps/EngineDemoApp';
import { LoadingPhase } from '../engine/core/LoadingManager';
import { InputEventManager } from '../engine/core/InputEventManager';
import { UIUpdateManager } from '../ui/UIUpdateManager';
import { AdvancedPOICamera } from '../engine/camera/AdvancedPOICamera';
import { FlyCamera } from '../engine/cameras/FlyCamera';
import { POI } from '../engine/poi/POI';
import { POIData } from '../engine/poi/POIData';
import { Color, Vector3, TextRenderMode } from '../engine/types';
import { MercatorProjection } from '../engine/terrain/MercatorProjection';
import { TerrainTileSource } from '../engine/terrain/TerrainTileSource';
import { TerrainRoot } from '../engine/terrain/TerrainRoot';
import { TerrainSockel } from '../engine/terrain/TerrainSockel';
import { TerrainGewaesser } from '../engine/terrain/TerrainGewaesser';
import { TerrainOrtsnamen } from '../engine/terrain/TerrainOrtsnamen';
import { TerrainTracks } from '../engine/terrain/TerrainTracks';
import { TiledTerrainSurface } from '../engine/map/surfaces/TiledTerrainSurface';
import { MapExperienceController } from '../engine/map/MapExperienceController';
import { MapTapController } from '../engine/map/MapTapController';
import { MapUserPositionRenderer } from '../engine/map/MapUserPositionRenderer';
import { configureMapPresentationSettings } from '../engine/map/MapPresentationSettings';
import { MapBridge } from '../embed/MapBridge';
import { TerrainPins } from './TerrainPins';
import { TerrainRouten } from './TerrainRouten';

export interface TerrainAppOptions {
    hoehenUrl?: string;
    orthoUrl?: string;
    /** Kachel-Existenzbereiche je Zoom — verhindert 404-Stuerme (#4729 P0). */
    boundsUrl?: string;
    bounds?: { minLon: number; minLat: number; maxLon: number; maxLat: number };
    origin?: { lon: number; lat: number };
    minZoom?: number;
    maxZoom?: number;
    /** Region-specific cut-out and overlays. null disables all regional extras. */
    region?: TerrainRegionConfig | null;
}

export interface TerrainRegionConfig {
    maskeUrl?: string | null;
    umrissUrl?: string | null;
    gewaesserUrl?: string | null;
    sockelEnabled?: boolean;
    waterwaysEnabled?: boolean;
    /** Kuratierte Regions-Beschriftung (Gold-Cinzel-Lettern). */
    ortsnamenUrl?: string | null;
    /** Track-Register (LOD-Einheiten: Emblem/Silhouette/Detail nach Distanz). */
    tracksUrl?: string | null;
    /** typeface.json fuer die Beschriftung (map-capture/cinzel-typeface.mjs). */
    fontUrl?: string | null;
}

/** Default region for the standalone terrain demo. Embedded/tenant compositions
 * can supply a complete region object or null to disable all regional extras. */
const KAERNTEN_REGION: Required<TerrainRegionConfig> = {
    maskeUrl: 'https://kaernten.arkserver.arkturian.com/kaernten/tiles/maske.png',
    umrissUrl: 'https://kaernten.arkserver.arkturian.com/3d/umriss-kaernten.json',
    gewaesserUrl: 'https://kaernten.arkserver.arkturian.com/kaernten/tiles/gewaesser.json',
    sockelEnabled: true,
    waterwaysEnabled: true,
    ortsnamenUrl: 'https://kaernten.arkserver.arkturian.com/3d/orte-kaernten.json',
    tracksUrl: 'https://kaernten.arkserver.arkturian.com/3d/tracks-kaernten.json',
    fontUrl: 'https://kaernten.arkserver.arkturian.com/3d/cinzel.typeface.json',
};

const DISABLED_REGION: Required<TerrainRegionConfig> = {
    maskeUrl: null,
    umrissUrl: null,
    gewaesserUrl: null,
    sockelEnabled: false,
    waterwaysEnabled: false,
    ortsnamenUrl: null,
    fontUrl: null,
    tracksUrl: null,
};

function resolveRegionConfig(region: TerrainRegionConfig | null | undefined): Required<TerrainRegionConfig> {
    if (region === undefined) return { ...KAERNTEN_REGION };
    if (region === null) return { ...DISABLED_REGION };
    return {
        maskeUrl: region.maskeUrl ?? null,
        umrissUrl: region.umrissUrl ?? null,
        gewaesserUrl: region.gewaesserUrl ?? null,
        sockelEnabled: region.sockelEnabled ?? Boolean(region.umrissUrl),
        waterwaysEnabled: region.waterwaysEnabled ?? Boolean(region.gewaesserUrl),
        ortsnamenUrl: region.ortsnamenUrl ?? null,
        fontUrl: region.fontUrl ?? null,
        tracksUrl: region.tracksUrl ?? null,
    };
}

/**
 * TerrainApp — Web-Mercator-Kachel-Terrain in der 3DPresenter-Engine.
 *
 * Erster Mieter: Kaernten (eigener basemap.at-Orthofoto-Baum + DGM-10m-
 * Heightmaps von kaernten.arkserver.arkturian.com, siehe map-capture).
 * Aufbau nach dem KoralmApp-Muster; die Kachelwelt haengt als EIN
 * TerrainRoot-SceneNode in der Szene.
 *
 * Massstab: 1 Unit = 5000 m — ganz Kaernten (~290 km) wird ~58 Units und
 * bleibt samt Kamera-Distanzen innerhalb der Engine-Konventionen
 * (Clipplanes 0.01/1000, cameraDistance-Bounds 0.01..20).
 */
export class TerrainApp extends EngineDemoApp {
    private readonly options: TerrainAppOptions;
    public terrainRoot: TerrainRoot | null = null;
    private _surface: TiledTerrainSurface | null = null;
    private _pins: TerrainPins | null = null;
    private _routes: TerrainRouten | null = null;
    private _bridge: MapBridge | null = null;
    private _experience: MapExperienceController | null = null;
    private _tapController: MapTapController | null = null;
    private _userPosition: MapUserPositionRenderer | null = null;
    private _targetPosition = new Vector3(0, 0.13, 0);
    private _focusedPin: { id: number; distance?: number } | null = null;
    private _gewaesser: TerrainGewaesser | null = null;
    private _tracks: TerrainTracks | null = null;
    private _trackLodHasTracks = false;
    private _activeDetailTrackId: number | null = null;
    private _trackRequestId = 0;
    private _trackPinsLoaded = false;
    private _trackRoutesLoaded = false;
    private _isEmbedMode = false;

    constructor(canvas: HTMLCanvasElement, cssContainer: HTMLElement, options: TerrainAppOptions = {}) {
        super(canvas, cssContainer);
        this.suppressDemoLoad = true;
        this.options = options;

        this.appSettings.hidePinSourceMeshes.value = false;
        this.appSettings.cameraDistance.value = 3.4;
        /* Karten-Perspektive: von schraeg oben, nicht vom Horizont — sonst
         * wird das Terrain beim Rauszoomen zum kantigen Streifen. */
        this.appSettings.cameraLatitude.value = 52;
        this.appSettings.cameraLongitude.value = 180;
        this.appSettings.enableBillboardForMapAnchors.value = false;
        this.appSettings.shapeBillboardYawOnly.value = false;
        this.appSettings.showConnector.value = false;
        this.appSettings.labelRenderMode.value = TextRenderMode.SCREEN_HTML_STATIC;
        /* Scene-Defaults wie MapPinsApp (Zeile 278-284) — die Pin-Optik der
         * echten Karte haengt an diesen Werten, nicht nur an den POIData. */
        this.sceneSettings.enableBillboard.value = true;
        this.sceneSettings.depth.value = 0.1;
        this.sceneSettings.cornerRadius.value = 0.2;
        this.sceneSettings.edgeRadius.value = 0.3;
        /* Terrain-Pins sind Kartenmarker, keine lineare Presenter-Story.
         * Ohne diese harte App-Grenze verbindet die generische ProcessCurve
         * alle nativen POI-Kinder mit einer weißen Linie und Stationsnummern
         * (Feldbefund 119650). Persistierte Presenter-Settings dürfen diese
         * map-spezifische Entscheidung nicht wieder einschalten. */
        configureMapPresentationSettings(this.sceneSettings);
    }

    protected override async onInitialize(): Promise<void> {
        if (!this.scene) {
            this.addError('Scene not initialized');
            return;
        }

        this.loadingManager.startPhase(LoadingPhase.SceneSetup, '🏔️ Terrain-Szene wird aufgebaut…');
        this.loadingManager.addTask('input-setup', 'Input Event Manager', 1);

        if (this.canvas) {
            InputEventManager.Instance.setCanvas(this.canvas);
            InputEventManager.Instance.setScene(this.scene.getThreeScene());

            // Mausrad-Zoom wie KoralmApp: multiplikativ auf cameraDistance
            this.canvas.addEventListener(
                'wheel',
                (event) => {
                    event.preventDefault();
                    const prop = this.appSettings?.cameraDistance;
                    if (!prop) return;
                    const aktuell = prop.value ?? 1.2;
                    const faktor = Math.exp(event.deltaY * 0.0012);
                    const min = prop.boundsMin ?? 0.01;
                    const max = prop.boundsMax ?? 20;
                    prop.value = Math.max(min, Math.min(max, aktuell * faktor));
                },
                { passive: false },
            );
            this.canvas.tabIndex = 0;
            this.canvas.style.outline = 'none';
            this.canvas.addEventListener('click', () => this.canvas?.focus());
            this.canvas.focus();
        }
        this.loadingManager.completeTask('input-setup');
        this.sceneManager?.addSceneNode(new UIUpdateManager());
        this.loadingManager.completePhase();

        this.loadingManager.startPhase(LoadingPhase.CameraSetup, '🎥 Kamera…');
        this.loadingManager.addTask('camera-creation', 'Advanced POI Camera', 1);
        this.camera = new AdvancedPOICamera(this.scene.getThreeScene(), this.appSettings, this.systemState);
        this.scene.setCamera(this.camera.internalCamera);
        InputEventManager.Instance.setCamera(this.camera.internalCamera);

        const flyCamera = new FlyCamera('SpectatorCamera', this.appSettings);
        (this as any).flyCamera = flyCamera;
        this.cameraManager.addCamera(flyCamera);
        if (this.camera?.internalCamera) {
            flyCamera.setRenderCamera(this.camera.internalCamera);
        }
        this.loadingManager.completeTask('camera-creation');
        this.loadingManager.completePhase();

        this.setupLayoutSystem();

        this.loadingManager.startPhase(LoadingPhase.POILoading, '🗺️ Kachelwelt…');
        this.loadingManager.addTask('terrain-setup', 'Terrain-Wurzeln', 2);
        this.loadingManager.addTask('terrain-shader-warmup', 'Terrain-Shader', 1);

        const o = this.options;
        const region = resolveRegionConfig(o.region);
        const embed = MapBridge.isEmbedActive();
        this._isEmbedMode = embed;
        const origin = o.origin ?? { lon: 14.2696, lat: 46.4859 };   // Tscheppaschlucht
        const bounds = o.bounds ?? { minLon: 12.65, minLat: 46.36, maxLon: 15.06, maxLat: 47.15 };
        const projection = new MercatorProjection({
            originLon: origin.lon,
            originLat: origin.lat,
            unitsPerMeter: 1 / 5000,
        });
        /* Handy-Profil: weniger Parallel-Loads, kleinere Split-Schwelle
         * (weniger gleichzeitige Kacheln), kein z+2-Komposit (Bandbreite). */
        const mobil = matchMedia('(pointer: coarse)').matches;
        const source = new TerrainTileSource({
            hoehenUrl: o.hoehenUrl ?? 'https://kaernten.arkserver.arkturian.com/kaernten/terrain/{z}/{x}/{y}.png',
            orthoUrl: o.orthoUrl ?? 'https://kaernten.arkserver.arkturian.com/kaernten/tiles/{z}/{x}/{y}.jpg',
            boundsUrl: o.boundsUrl ?? 'https://kaernten.arkserver.arkturian.com/kaernten/tiles/kachel-bounds.json',
            maxParallel: mobil ? 4 : 8,
            orthoDetailOffset: mobil ? 1 : 2,
        });
        this.terrainRoot = new TerrainRoot({
            projection,
            source,
            bounds,
            /* Regions-Cut-out: Kaernten-Umriss (maske-backen.mjs) — Teil der
             * Region-Config, jede Region bringt ihr eigenes Polygon mit */
            maskeUrl: region.maskeUrl ?? undefined,
            minZoom: o.minZoom ?? 7,
            /* z15 flaechig + z16 Schlucht-Insel liegen als Heightmaps auf dem
             * Server; die LOD-Randregel haelt Kacheln ohne tiefere Stufe
             * automatisch auf ihrem Blatt-Level. */
            maxZoom: o.maxZoom ?? 16,
            splitSchwelle: mobil ? 1.1 : 0.8,
        });
        if (this.camera?.internalCamera) this.terrainRoot.setCamera(this.camera.internalCamera);
        this.sceneManager?.addSceneNode(this.terrainRoot);
        try {
            await this.terrainRoot.warmupShaders(
                this.scene.getRenderer(),
                this.scene.getThreeScene(),
                this.scene.getCamera(),
            );
        } catch (error) {
            // Optimierung, nie Start-Blocker: der normale Renderpfad bleibt intakt.
            console.warn('[terrain] Shader-Warmup fehlgeschlagen — lazy compile bleibt aktiv:', error);
        } finally {
            this.loadingManager.completeTask('terrain-shader-warmup');
        }

        // Sockel: Seitenwand + Bodenplatte entlang des Regions-Umrisses
        // (Alex 2026-08-15: Rand soll nicht in der Luft haengen)
        if (region.sockelEnabled && region.umrissUrl) {
            const sockel = new TerrainSockel({
                terrain: this.terrainRoot,
                umrissUrl: region.umrissUrl,
            });
            this.sceneManager?.addSceneNode(sockel);
        }

        /* Gewaessernetz (Fluesse/Baeche) als gedrapte Baender — opt-in,
         * gleiche gewaesser.json wie der 2D-WaterwayOverlayRenderer.
         * Einblenden: ?wasser=1 oder setGewaesserSichtbar(true). */
        if (region.waterwaysEnabled && region.gewaesserUrl) {
            this._gewaesser = new TerrainGewaesser({
                terrain: this.terrainRoot,
                url: region.gewaesserUrl,
                umrissUrl: region.umrissUrl ?? undefined,
            });
            this.sceneManager?.addSceneNode(this._gewaesser);
            if (new URL(location.href).searchParams.get('wasser') === '1') {
                this._gewaesser.setSichtbar(true);
            }
        }

        /* Regions-Beschriftung: Gold-Cinzel-Lettern flach auf dem Relief
         * (Museums-Kartenmodell). Permanent sichtbar — Teil der Karte. */
        if (region.ortsnamenUrl && region.fontUrl) {
            this.sceneManager?.addSceneNode(new TerrainOrtsnamen({
                terrain: this.terrainRoot,
                orteUrl: region.ortsnamenUrl,
                fontUrl: region.fontUrl,
                getRenderer: () => this.scene!.getRenderer(),
            }));
        }

        /* Track-LOD: weit draussen schlaeft das Track-Detail (Pins, Sim,
         * Baender) komplett — sichtbar bleibt ein goldenes Siegel, ab
         * mittlerer Distanz die Routen-Silhouette. Der Stufen-Callback
         * schaltet die Detail-Nodes; Schwellen stehen im Register. */
        if (region.tracksUrl) {
            this._tracks = new TerrainTracks({
                terrain: this.terrainRoot,
                // v0.5: embedded frames receive domain data from the host.
                registerUrl: embed ? null : region.tracksUrl,
                getRenderer: () => this.scene!.getRenderer(),
                getKamera: () => this.camera?.internalCamera ?? null,
                istKameraInFahrt: () => this.camera?.isTravelling() ?? false,
                onAktiverDetailTrack: trackId => this._onActiveDetailTrack(trackId),
                onRegisterChanged: count => this._onTrackRegisterChanged(count),
                onInvalidTrack: message => {
                    console.error('[terrain]', message);
                    this._bridge?.sendError('TRACK_INVALID', message);
                },
            });
            this.sceneManager?.addSceneNode(this._tracks);
        }

        this._surface = new TiledTerrainSurface({
            terrain: this.terrainRoot,
            bounds,
            onHeightsChanged: listener => this.terrainRoot!.onHeightsChanged(listener),
        });

        // Echte 3D-Pins mit Realtime-Force-Graph (Track-30-POIs) — engine-nativ
        const pins = new TerrainPins({
            terrain: this.terrainRoot,
            surface: this._surface,
            sceneSettings: this.sceneSettings,
            appSettings: this.appSettings,
            layoutData: this.getLayoutData(),
            autoLoad: !embed,
        });
        if (this.camera?.internalCamera) pins.setCamera(this.camera.internalCamera);
        this.sceneManager?.addSceneNode(pins);
        (this as any).terrainPins = pins;
        this._pins = pins;

        // Routen als Pfad-Baender (MeshLineToPlane-Verfahren, live gedrapt)
        const routen = new TerrainRouten({ terrain: this.terrainRoot, autoLoad: !embed });
        this.sceneManager?.addSceneNode(routen);
        (this as any).terrainRouten = routen;
        this._routes = routen;

        /* Track-LOD regiert: Detail beginnt SCHLAFEND — der erste
         * TerrainTracks-Zyklus (ohne Stabilfenster) weckt es, wenn die
         * Kamera nah genug startet. Ohne Register bleibt alles an. */
        if (this._tracks) {
            pins.setAktiv(false);
            routen.setAktiv(false);
        }

        this._userPosition = new MapUserPositionRenderer({
            surface: this._surface,
            parent: this.terrainRoot.getThreeObjects()[0],
        });

        // Ein Anker-POI im Projektionsursprung gibt der Orbit-Kamera ihr Ziel.
        const anker = this._erzeugeAnkerPoi();
        this.addPOI(anker);
        this.setActivePOI(anker);
        (this as any)._terrainAnker = anker;

        if (embed) this._startBridge();

        this.loadingManager.completeTask('terrain-setup');
        this.loadingManager.completePhase();

        this.loadingManager.startPhase(LoadingPhase.Ready, '🚀 Start…');
        this.loadingManager.addTask('final-setup', 'Abschluss', 1);
        this.loadingManager.completeTask('final-setup');
        this.loadingManager.complete();
        await this.loadingManager.waitUntilReady();
    }

    private _erzeugeAnkerPoi(): POI {
        const data = new POIData(this.sceneSettings, this.appSettings);
        data.shape.value = 'sphere';
        data.scale.value = 0.004;
        data.showLabel.value = false;
        data.showConnector.value = false;
        data.color.value = new Color('#C4A96A');
        // Ursprung = Projektionsursprung; Hoehe grob auf Schluchtboden
        data.localTranslationOffset.value = new Vector3(0, 0.13, 0);
        const layoutData = this.getLayoutData();
        layoutData.fieldAwarenessEnabled.value = false;
        return new POI('Terrain-Anker', data, this.sceneSettings, this.appSettings, layoutData);
    }

    private _startBridge(): void {
        if (!this._surface || !this._pins || !this._routes || !this._userPosition || this._bridge) return;
        this._experience = new MapExperienceController({
            surface: this._surface,
            onInit: () => undefined,
            pins: {
                setPins: async pins => {
                    await this._pins!.setBridgePins(pins);
                    this._trackPinsLoaded = true;
                    this._syncTrackDetailVisibility();
                },
                focusPoi: (id, zoom) => this._focusPin(id, zoom),
                setActivePoi: id => this._pins?.setActivePin(id),
                setCategoryFilter: categories => this._pins?.setCategoryFilter(categories),
                refreshSurface: () => {
                    this._pins?.refreshSurface();
                    // Refined terrain may move the ground anchor, but it must not
                    // reset a distance the user changed via wheel/pinch meanwhile.
                    if (this._focusedPin) this._focusPin(this._focusedPin.id, undefined, false);
                },
            },
            routes: {
                setRoutes: routes => {
                    this._routes?.setBridgeRoutes(routes);
                    this._trackRoutesLoaded = true;
                    this._syncTrackDetailVisibility();
                },
                selectRoute: routeId => this._routes?.selectRoute(routeId),
                refreshSurface: () => this._routes?.refreshSurface(),
            },
            userPosition: {
                setPosition: position => { this._userPosition?.setPosition(position); },
                refreshSurface: () => { this._userPosition?.refreshSurface(); },
            },
            camera: {
                flyTo: payload => {
                    const world = this._surface?.geoToWorld(payload.lat, payload.lng);
                    if (!world) return;
                    const ground = this._surface?.getWorldY(world.x, world.z);
                    if (ground !== null && ground !== undefined) world.y = ground;
                    this._focusWorld(world, payload.distance);
                },
                zoomBy: factor => {
                    const property = this.appSettings.cameraDistance;
                    const current = property.value ?? 3.4;
                    const min = property.boundsMin ?? 0.01;
                    const max = property.boundsMax ?? 20;
                    property.value = THREE.MathUtils.clamp(current / factor, min, max);
                },
            },
            overlays: this._gewaesser ? {
                setWaterwaysVisible: visible => this.setGewaesserSichtbar(visible),
                refreshSurface: () => this._gewaesser?.refreshSurface(),
            } : undefined,
            tracks: this._tracks ? {
                setTracks: tracks => { this._tracks?.setTracks(tracks); },
            } : undefined,
        });
        this._bridge = new MapBridge(this._experience, {
            additionalCapabilities: [
                ...(this._gewaesser ? ['waterways'] : []),
                ...(this._tracks ? ['tracks'] : []),
            ],
        });
        this._bridge.start();

        if (this.canvas && this.camera?.internalCamera) {
            this._tapController = new MapTapController(
                this.canvas,
                (x, y) => this._pins?.pick(x, y, this.camera!.internalCamera, this.canvas!) ?? null,
                id => this._bridge?.sendPoiClicked(id),
            );
            this._tapController.start();
        }
    }

    /** Gewaesser-Ebene (Fluesse/Baeche) ein-/ausblenden — Host-/Debug-Zugang. */
    setGewaesserSichtbar(sichtbar: boolean): void {
        this._gewaesser?.setSichtbar(sichtbar);
    }

    private _onTrackRegisterChanged(validTrackCount: number): void {
        this._trackLodHasTracks = validTrackCount > 0;
        if (!this._trackLodHasTracks) {
            this._activeDetailTrackId = null;
            this._trackPinsLoaded = false;
            this._trackRoutesLoaded = false;
        }
        this._syncTrackDetailVisibility();
    }

    private _onActiveDetailTrack(trackId: number | null): void {
        if (trackId === this._activeDetailTrackId) return;
        const previous = this._activeDetailTrackId;
        this._activeDetailTrackId = trackId;
        this._trackPinsLoaded = false;
        this._trackRoutesLoaded = false;

        if (this._isEmbedMode) {
            const requestId = ++this._trackRequestId;
            if (previous !== null) this._bridge?.sendTrackDetailReleased(previous, requestId);
            if (trackId !== null) this._bridge?.sendTrackDetailRequested(trackId, requestId);
        }
        this._syncTrackDetailVisibility();
    }

    private _syncTrackDetailVisibility(): void {
        if (!this._pins || !this._routes) return;
        if (!this._trackLodHasTracks) {
            this._pins.setAktiv(true);
            this._routes.setAktiv(true);
            return;
        }
        const detailActive = this._activeDetailTrackId !== null;
        this._pins.setAktiv(detailActive && (!this._isEmbedMode || this._trackPinsLoaded));
        this._routes.setAktiv(detailActive && (!this._isEmbedMode || this._trackRoutesLoaded));
    }

    private _focusPin(id: number, distance?: number, reportMissing = true): void {
        const world = this._pins?.getPinWorldPosition(id);
        if (!world) {
            if (reportMissing) this._bridge?.sendError('POI_NOT_FOUND', `focusPoi(${id})`);
            return;
        }
        this._focusedPin = { id, distance };
        this._focusWorld(world, distance);
    }

    private _focusWorld(world: THREE.Vector3, distance?: number): void {
        this._targetPosition = new Vector3(world.x, world.y, world.z);
        const anchor = (this as any)._terrainAnker as POI | undefined;
        if (anchor) {
            anchor.transform.position.setImmediate(this._targetPosition);
            this.setActivePOI(anchor);
        }
        if (typeof distance === 'number' && Number.isFinite(distance) && distance > 0) {
            const property = this.appSettings.cameraDistance;
            property.value = THREE.MathUtils.clamp(
                distance,
                property.boundsMin ?? 0.01,
                property.boundsMax ?? 20,
            );
        }
    }

    protected override onUpdate(deltaTime: number): void {
        super.onUpdate?.(deltaTime);
        /* Der Line-Layouter schiebt POIs an Layout-Positionen (-3,0,-1.5 beim
         * ersten Element) — die Orbit-Kamera kreiste dadurch 17 km neben der
         * Schlucht. Der Terrain-Anker gehoert IMMER in den Projektions-
         * ursprung; hier jeden Frame festnageln ist billig und layoutfest. */
        const anker = (this as any)._terrainAnker;
        if (anker) {
            const pos = anker.transform.position.value;
            const target = this._targetPosition;
            if (!pos || pos.x !== target.x || pos.y !== target.y || pos.z !== target.z) {
                anker.transform.position.setImmediate(target);
            }
        }
        // Kamera nicht unter das Gelaende fallen lassen
        const kamera = this.camera?.internalCamera as THREE.PerspectiveCamera | undefined;
        if (kamera && this.terrainRoot) {
            const pos = kamera.position;
            const bodenY = this.terrainRoot.getWorldY(pos.x, pos.z);
            const mindestAbstand = 0.02;      // 100 m bei 1u=5km
            if (bodenY !== null && pos.y < bodenY + mindestAbstand) {
                pos.y = bodenY + mindestAbstand;
            }
            if (this._bridge && this._surface) {
                const geo = this._surface.worldToGeo(pos);
                if (geo) {
                    const forward = new THREE.Vector3();
                    kamera.getWorldDirection(forward);
                    const heading = THREE.MathUtils.radToDeg(Math.atan2(forward.x, -forward.z));
                    const pitch = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(forward.y, -1, 1)));
                    this._bridge.sendCameraChanged({
                        lat: geo.lat,
                        lng: geo.lng,
                        distance: this.appSettings.cameraDistance.value ?? 3.4,
                        heading,
                        pitch,
                    });
                }
            }
        }
    }
}
